import { useState, useEffect, useCallback, useRef } from 'react';
import { throttle } from 'lodash';

/**
 * スクロール位置に基づいて現在表示しているpdfのページを検出するためのカスタムフック。
 *
 * - パフォーマンスのため、throttleを使用してイベントの発火回数を制限している
 *
 * @param {number} pageNumber - ドキュメント内のページの総数。
 *
 * @returns {Object} オブジェクトは以下を含む:
 * - {number} currentPage: 現在表示されているページの番号
 * - {Object} documentRef: Documentコンポーネントのためのref
 * - {Object[]} pageRefs: 各Pageコンポーネントのためのrefの配列
 */
const useDetectCurrentPage = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const documentRef = useRef(null);
  const pageRefs = useRef([]);

  const handleScroll = useCallback(() => {
    const containerCenterY = window.innerHeight / 2;
    let closestPageNum = 1;
    let minDistance = Infinity;

    pageRefs.current.forEach((pageRef, index) => {
      const rect = pageRef.getBoundingClientRect();
      const pageCenterY = rect.top + rect.height / 2;
      const distance = Math.abs(containerCenterY - pageCenterY);

      if (distance < minDistance) {
        minDistance = distance;
        closestPageNum = index + 1;
      }
    });
    setCurrentPage(closestPageNum);
  }, []);

  const throttledOnScroll = throttle(handleScroll, 500);

  useEffect(() => {
    const scrollArea = documentRef.current;
    if (scrollArea) {
      scrollArea.addEventListener('scroll', throttledOnScroll);
    }

    return () => {
      if (scrollArea) {
        scrollArea.removeEventListener('scroll', throttledOnScroll);
      }
    };
  }, [throttledOnScroll, documentRef]);

  return { currentPage, documentRef, pageRefs };
};

export default useDetectCurrentPage;
